<template>
  <Modal :show="show">
    <template #header>
      <div class="d-flex flex-fill w-100 p-0">
        <span class="ms-auto me-0 right p-0" @click="$emit('close')">
          <i class="fa fa-times" style="font-size: larger !important"></i>
        </span>
      </div>
      <div class="d-flex flex-row">
        <h5
          class="text-black fw-bolder text-center me-1 w-100"
          id="modal-title-header"
        >
          {{ headerText }}
        </h5>
      </div>
      <p
        class="w-100 text-black text-center"
        v-if="step === ModalSteps.CONFIRM_ADDRESS"
      >
        {{
          t("PAYJOY_CARD_VIEWS.REPORT_UNDELIVERED_CONFIRM_ADDRESS_DESCRIPTION")
        }}
      </p>
    </template>
    <template #body>
      <p
        v-if="step === ModalSteps.CARD_NOT_RECEIVED"
        class="text-center text-black fs-6"
      >
        {{ t("PAYJOY_CARD_VIEWS.REPORT_UNDELIVERED_DESCRIPTION") }}
      </p>
      <p
        v-if="step === ModalSteps.INFO_CLOSE"
        class="text-center p-3 text-black fs-6"
      >
        {{ t("PAYJOY_CARD_VIEWS.REPORT_UNDELIVERED_THANKS_DESCRIPTION") }}
      </p>
      <hr
        class="w-100"
        :hidden="
          step === ModalSteps.CONFIRM_ADDRESS ||
          step === ModalSteps.DEACTIVATE_CARD
        "
      />
    </template>
    <template #footer>
      <button
        v-if="step !== ModalSteps.INFO_CLOSE"
        class="mt-3 text-center fw-bold modal-btn"
        @click="handleContinue"
      >
        {{ continueButtonText }}
      </button>
      <button
        v-if="step !== ModalSteps.INFO_CLOSE"
        class="mt-3 text-center fw-bold modal-btn"
        @click="handleCancel"
      >
        {{ cancelButtonText }}
      </button>
      <button
        v-if="step === ModalSteps.INFO_CLOSE"
        class="mt-3 text-center fw-bold mx-auto modal-btn"
        @click="$emit('close')"
      >
        {{ t("PAYJOY_CARD_VIEWS.REPORT_UNDELIVERED_CLOSE_BTN") }}
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Modal from "@/apps/credit-line/components/Modal.vue";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { useStore } from "vuex";

const { t } = useI18n();
const CreditLineService = new Service();

enum ModalSteps {
  CARD_NOT_RECEIVED,
  CONFIRM_ADDRESS,
  INFO_CLOSE,
  DEACTIVATE_CARD,
}

interface Props {
  show: boolean;
  payjoyCardStatus: PayJoyCardStatus;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  addressInfo?: any;
  shipmentId?: string;
}

const store = useStore();

const props = withDefaults(defineProps<Props>(), {
  show: false,
  payjoyCardStatus: PayJoyCardStatus.NONE,
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "update:show", value: boolean): void;
  (e: "cardReported"): void;
}>();

const step = ref<ModalSteps>(ModalSteps.CARD_NOT_RECEIVED);
const headerText = ref<string>("");

watch(() => props.payjoyCardStatus, updateModalState);
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      updateModalState();
    }
  }
);

function updateModalState() {
  if (props.payjoyCardStatus === PayJoyCardStatus.DELIVERED) {
    step.value = ModalSteps.CARD_NOT_RECEIVED;
    headerText.value = t("PAYJOY_CARD_VIEWS.REPORT_UNDELIVERED_QUESTION");
  } else {
    step.value = ModalSteps.DEACTIVATE_CARD;
    headerText.value = t("CONFIRMTOGGLECARDOFF");
  }
}

const continueButtonText = computed(() => {
  if (props.payjoyCardStatus === PayJoyCardStatus.DELIVERED) {
    return step.value === ModalSteps.CARD_NOT_RECEIVED
      ? t("PAYJOY_CARD_VIEWS.REPORT_UNDELIVERED_CONTINUE_MODAL_BTN")
      : t("YES");
  }
  return t("YES");
});

const cancelButtonText = computed(() => {
  if (props.payjoyCardStatus === PayJoyCardStatus.DELIVERED) {
    return step.value === ModalSteps.CARD_NOT_RECEIVED
      ? t("PAYJOY_CARD_VIEWS.REPORT_UNDELIVERED_CANCEL_MODAL_BTN")
      : t("NO");
  }
  return t("CANCEL");
});

const handleContinue = async () => {
  if (step.value === ModalSteps.CARD_NOT_RECEIVED) {
    if (!props.addressInfo) {
      await sendReportCardNotReceived(false);
    } else {
      step.value = ModalSteps.CONFIRM_ADDRESS;
      headerText.value = t(
        "PAYJOY_CARD_VIEWS.REPORT_UNDELIVERED_CONFIRM_ADDRESS"
      );
    }
  } else if (step.value === ModalSteps.CONFIRM_ADDRESS) {
    await sendReportCardNotReceived(true);
  } else if (step.value === ModalSteps.DEACTIVATE_CARD) {
    emit("close");
  }
};

const handleCancel = async () => {
  if (
    step.value === ModalSteps.DEACTIVATE_CARD ||
    step.value === ModalSteps.CARD_NOT_RECEIVED
  ) {
    emit("close");
  } else {
    await sendReportCardNotReceived(false);
  }
};

const sendReportCardNotReceived = async (correctAddressInCcReport: boolean) => {
  if (props.shipmentId) {
    await CreditLineService.reportNotReceivedCard({
      deviceTag: AuthenticationHelper.getDeviceTag() as string,
      shipmentId: props.shipmentId,
      correctAddressInCcReport,
      creditLineId: store.state?.creditLine?.summary?.creditLineId,
    });
    step.value = ModalSteps.INFO_CLOSE;
    headerText.value = t("PAYJOY_CARD_VIEWS.REPORT_UNDELIVERED_THANKS_TITLE");
    emit("cardReported");
  }
};
</script>

<style scoped>
.modal-btn {
  width: 45%;
  border-radius: 10px;
  background: var(--main-primary-500, #2c7040);
  color: var(--main-white, #fff);
  height: 44px;
  text-align: center;
  font-size: medium;
  border: none;
}
</style>
